import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { AccessList, roleLabel, scopeLabel } from '../components/ShareDialog';
import type { Share, SharedWithMe } from '../types';

/** /sharing — everything you shared (change level, remove) and what others shared with you. */
export default function SharingPage() {
  const [mine, setMine] = useState<Share[] | null>(null);
  const [withMe, setWithMe] = useState<SharedWithMe[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    Promise.all([api.shares(), api.sharedWithMe()])
      .then(([a, b]) => {
        setMine(a.shares);
        setWithMe(b.shares);
      })
      .catch((err) => setError((err as Error).message));
  }, []);
  useEffect(load, [load]);

  const leave = async (id: string) => {
    try {
      await api.deleteShare(id);
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <h1 className="mb-1 text-3xl font-extrabold tracking-tight">Sharing</h1>
      <p className="mb-8 text-sm text-white/50">
        To share, open an episode and press <span className="text-white/80">Share</span>. You choose the episode, its season or your whole library.
      </p>
      {error && (
        <p role="alert" className="mb-4 text-sm text-red-300">
          {error}
        </p>
      )}

      <div className="mb-12 rounded-xl bg-white/[0.03] p-5 ring-1 ring-white/10">
        <AccessList shares={mine} onChanged={load} onError={setError} title="You shared" />
      </div>

      <section aria-label="Shared with you" className="rounded-xl bg-white/[0.03] p-5 ring-1 ring-white/10">
        <h2 className="mb-2 text-sm font-semibold tracking-wide text-white/50 uppercase">Shared with you</h2>
        {withMe === null ? (
          <p className="text-sm text-white/40">Loading…</p>
        ) : withMe.length === 0 ? (
          <p className="text-sm text-white/45">Nothing yet. When someone sends you an invite link, it shows up here.</p>
        ) : (
          <ul className="divide-y divide-white/[0.06]">
            {withMe.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <Link
                    to={
                      s.scope === 'EPISODE' && s.episode
                        ? `/episode/${s.episode.id}`
                        : s.scope === 'SEASON'
                          ? `/season/${s.seasonYear}?owner=${s.owner.id}`
                          : '/'
                    }
                    className="focus-ring truncate rounded text-sm font-medium hover:underline"
                  >
                    {scopeLabel(s)}
                  </Link>
                  <p className="text-xs text-white/45">
                    From {s.owner.name} · {roleLabel(s.role)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void leave(s.id)}
                  className="focus-ring rounded-md px-2.5 py-1.5 text-xs text-white/55 ring-1 ring-white/10 hover:bg-white/10 hover:text-white"
                >
                  Leave
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
